import { c as _c } from "react/compiler-runtime";
import insights from 'search-insights';
import type { ProductHit } from '@libs/algolia';
import { AlgoliaIndice, getAlgoliaInsightsInitOptions, isAlgoliaInsightsConfigured } from '@libs/algolia';
import { IS_SSR, type ItemsListItem, useDeviceType } from '@libs/common-next';
import type { ItemUI } from '@libs/common-node';
import { getCurrency, isUserLoggedIn, logger } from '@libs/core';
import { isDefinitivelyOptedOutTracking, ONE_TRUST_GROUPS_UPDATED_EVENT, waitForAlgoliaTrackingInitReadiness } from '../../helpers/algoliaAttributionReadiness';
import { addUpdateAlgoliaEvents, deleteEvents, getAlgoliaEvents, getMasterProductID, getPDPEventData, isOptedOutTracking } from '../../helpers/algoliaEventUtils';
import { getGaUserId } from '../../helpers/analytics';
import type { AlgoliaClickEvent, AutocompleteItem } from '../../types/algoliaTracking';

// OneTrust loads asynchronously and emits `OneTrustGroupsUpdated` once its
// consent groups (`globalThis.OnetrustActiveGroups`) are populated. The `_ga`
// cookie is written asynchronously by GTM/GA after page load. On a cold load
// neither is present yet, so we:
//   1. wait for OneTrust via its `OneTrustGroupsUpdated` event (or detect that
//      it's already loaded), then check opt-out
//   2. poll briefly for `_ga` so that `insights('init', { userToken })` uses
//      the real GA-derived token
// before any Algolia query runs. Both steps share a single 3 s budget so we
// always resolve quickly.
const ALGOLIA_INIT_GA_POLL_INTERVAL_MS = 100;
const ALGOLIA_INIT_MAX_WAIT_MS = 3000;
let algoliaInitPromise: Promise<void> | null = null;
let algoliaInsightsInitialized = false;
let isConsentListenerPending = false;
const resetAlgoliaInitPromiseForUpdatedConsent = (logMessage: string) => {
  logger.info(logMessage);
  algoliaInitPromise = null;
  algoliaInsightsInitialized = false;

  // Keep at most one pending consent self-heal listener. Since the reset nulls
  // `algoliaInitPromise`, subsequent init attempts aren't deduped by the
  // promise guard and would otherwise stack a new listener on every deferral.
  if (isConsentListenerPending) return;
  isConsentListenerPending = true;
  globalThis.addEventListener(ONE_TRUST_GROUPS_UPDATED_EVENT, () => {
    isConsentListenerPending = false;
    void initAlgoliaTracking();
  }, {
    once: true
  });
};
export const initAlgoliaTracking = (): Promise<void> => {
  if (IS_SSR) return Promise.resolve();
  if (!isAlgoliaInsightsConfigured()) {
    logger.warn('Algolia tracking disabled: Missing environment variables');
    return Promise.resolve();
  }
  if (algoliaInitPromise) return algoliaInitPromise;
  algoliaInitPromise = (async () => {
    try {
      const {
        didOneTrustLoad,
        hasOptedOut,
        userToken
      } = await waitForAlgoliaTrackingInitReadiness({
        maxWaitMs: ALGOLIA_INIT_MAX_WAIT_MS,
        pollIntervalMs: ALGOLIA_INIT_GA_POLL_INTERVAL_MS,
        getUserToken: () => getGaUserId('_ga')
      });
      if (!didOneTrustLoad) {
        resetAlgoliaInitPromiseForUpdatedConsent('Algolia tracking init deferred: OneTrust did not load within budget; will retry on consent update');
        return;
      }
      if (hasOptedOut) {
        resetAlgoliaInitPromiseForUpdatedConsent('Algolia tracking disabled: opted out via OneTrust; will retry on consent update');
        return;
      }
      insights('init', {
        ...getAlgoliaInsightsInitOptions(),
        userToken
      });
      algoliaInsightsInitialized = true;
    } catch (e) {
      // Clear the cached promise so the next caller can retry. We also
      // swallow the error rather than re-throwing so callers that chain
      // off this promise (e.g. the wrapped Algolia search client in
      // SearchLayout) don't fail their requests because a fire-and-forget
      // tracking init failed.
      algoliaInitPromise = null;
      algoliaInsightsInitialized = false;
      logger.error('Algolia tracking init failed', e);
    }
  })();
  return algoliaInitPromise;
};

// Exposed for tests; resets the cached init promise so each test starts fresh.
export const __resetAlgoliaTrackingForTests = () => {
  algoliaInitPromise = null;
  algoliaInsightsInitialized = false;
  isConsentListenerPending = false;
};
const isAlgoliaTrackingReady = () => algoliaInsightsInitialized && !isOptedOutTracking();
const runAfterAlgoliaTrackingInit = (track: () => void): void => {
  void initAlgoliaTracking().then(() => {
    if (isAlgoliaTrackingReady()) {
      track();
    }
  });
};
export const algoliaTrackClickEvent = (rawProductHit: ProductHit) => {
  if (isDefinitivelyOptedOutTracking()) {
    return;
  }
  if (!isAlgoliaInsightsConfigured()) {
    console.warn('Algolia tracking disabled: Missing environment variables');
    return;
  }
  runAfterAlgoliaTrackingInit(() => {
    try {
      if (rawProductHit.__queryID) {
        addUpdateAlgoliaEvents({
          objectID: rawProductHit.objectID,
          __queryID: rawProductHit.__queryID,
          masterProductID: rawProductHit.masterProductID
        }, false);
        insights('clickedObjectIDsAfterSearch', {
          eventName: 'PLP: Product Clicked',
          index: AlgoliaIndice.PRODUCTS,
          objectIDs: [rawProductHit.objectID],
          positions: [rawProductHit.__position],
          queryID: rawProductHit.__queryID,
          userToken: getGaUserId('_ga')
        });
      }
    } catch (e) {
      console.error('Error in algoliaTrackClickEvent:', e);
    }
  });
};

// Incoming product prices come in cents, convert to dollars for Algolia tracking
const convertCentsToDollars = (cents: number) => cents / 100;
const getPrice = (product: ItemUI) => convertCentsToDollars(product.listPrice);
const getDiscount = (product: ItemUI) => convertCentsToDollars(product.basePrice - product.listPrice);
const getPriceValue = (product: ItemUI, quantity: number) => {
  const price = getPrice(product);
  const cost = price * quantity;
  const value = Math.round(cost * 100) / 100;
  return value;
};
export const algoliaPDPTrackClickEvent = (product: ItemUI) => {
  if (isDefinitivelyOptedOutTracking()) {
    return;
  }
  if (!isAlgoliaInsightsConfigured()) {
    console.warn('Algolia tracking disabled: Missing environment variables');
    return;
  }

  // wrapped in a try/catch to avoid ATC issues.
  runAfterAlgoliaTrackingInit(() => {
    try {
      const objectID = product.sku;
      const masterProductID = getMasterProductID(product);
      const eventData = getPDPEventData(product);
      if (eventData?.queryID) {
        addUpdateAlgoliaEvents({
          objectID: objectID,
          __queryID: eventData.queryID,
          masterProductID: masterProductID
        }, true);
        insights('addedToCartObjectIDsAfterSearch', {
          userToken: getGaUserId('_ga'),
          eventName: 'PDP: Product Added to Cart after Search',
          index: AlgoliaIndice.PRODUCTS,
          objectIDs: [objectID],
          queryID: eventData.queryID,
          value: getPriceValue(product, product.quantity),
          currency: getCurrency(),
          objectData: [{
            price: getPrice(product),
            quantity: product.quantity
          }]
        });
      } else {
        insights('addedToCartObjectIDs', {
          userToken: getGaUserId('_ga'),
          eventName: 'PDP: Product Added to Cart',
          index: AlgoliaIndice.PRODUCTS,
          objectIDs: [objectID],
          value: getPriceValue(product, product.quantity),
          currency: getCurrency(),
          objectData: [{
            price: getPrice(product),
            quantity: product.quantity
          }]
        });
      }
    } catch (e) {
      console.error('Error in algoliaPDPTrackClickEvent:', e);
    }
  });
};
export const algoliaAutofillTrack = (rawProductHit: AutocompleteItem) => {
  if (isDefinitivelyOptedOutTracking()) {
    return;
  }
  if (!isAlgoliaInsightsConfigured()) {
    console.warn('Algolia tracking disabled: Missing environment variables');
    return;
  }
  runAfterAlgoliaTrackingInit(() => {
    try {
      if (rawProductHit.__autocomplete_queryID && rawProductHit.__autocomplete_id) {
        addUpdateAlgoliaEvents({
          objectID: rawProductHit.objectID,
          __queryID: rawProductHit.__autocomplete_queryID,
          masterProductID: rawProductHit.masterProductID
        }, false);
        insights('clickedObjectIDsAfterSearch', {
          eventName: 'Autocomplete: Product Clicked',
          index: AlgoliaIndice.PRODUCTS,
          objectIDs: [rawProductHit.objectID],
          positions: [rawProductHit.__autocomplete_id],
          queryID: rawProductHit.__autocomplete_queryID,
          userToken: getGaUserId('_ga')
        });
      }
    } catch (e) {
      console.error('Error in algoliaAutofillTrack:', e);
    }
  });
};
export const algoliaCheckoutTrackClickEvent = (purchasedProducts: ItemsListItem[]) => {
  if (!isAlgoliaInsightsConfigured()) {
    console.warn('Algolia tracking disabled: Missing environment variables');
    // Purchase insights can't be sent without env vars, but the attribution
    // cookie must still be cleared so stale events aren't carried to the
    // next order.
    deleteEvents();
    return;
  }

  // Snapshot the attribution events and clear the cookie synchronously. This
  // guarantees cleanup on every checkout regardless of init timing — even if
  // init is still deferred (up to ALGOLIA_INIT_MAX_WAIT_MS), opted out, or
  // fails, or the page is torn down (hard reload / redirect to a payment
  // provider) before the async init below resolves. Any events written after
  // this point belong to a new interaction and must not be attributed to the
  // completed order. The `finally` keeps cleanup guaranteed even if the read
  // throws.
  let algoliaEvents: AlgoliaClickEvent[] = [];
  try {
    algoliaEvents = getAlgoliaEvents();
  } catch (e) {
    console.error('Error in algoliaCheckoutTrackClickEvent:', e);
  } finally {
    deleteEvents();
  }

  // The cookie is already cleared above. If the user has definitively opted
  // out, stop here so a cleanup-only checkout doesn't call init (and arm a
  // consent self-heal listener) purely as a side effect.
  if (isDefinitivelyOptedOutTracking()) {
    return;
  }

  // Purchase insights still require a successful insights init and consent;
  // send them asynchronously once init resolves, using the snapshot above.
  void initAlgoliaTracking().then(() => {
    if (isAlgoliaTrackingReady()) {
      try {
        if (algoliaEvents && algoliaEvents.length > 0) {
          const addedToCart = algoliaEvents.filter(event => event.addedToCart);

          // Most efficient approach: iterate through purchased products and check algolia events
          const combinedObjectIDs: string[] = [];
          const combinedObjectData: Array<{
            queryID?: string;
            price: number;
            discount: number;
            quantity: number;
          }> = [];

          // Create a map of objectID -> algolia event for O(1) lookup
          const algoliaEventMap = new Map<string, (typeof addedToCart)[0]>();
          addedToCart.forEach(event => {
            if (event.objectIDs.length > 0 && event.addedToCart) {
              event.objectIDs.forEach(objectID => {
                algoliaEventMap.set(objectID, event);
              });
            }
          });

          // Iterate through purchased products and check if they have algolia events
          purchasedProducts.forEach(item => {
            const algoliaEvent = algoliaEventMap.get(item.sku);
            if (algoliaEvent) {
              combinedObjectIDs.push(item.sku);
              combinedObjectData.push({
                queryID: algoliaEvent.queryID,
                price: getPrice(item),
                discount: getDiscount(item),
                quantity: item.quantity
              });
            }
          });

          // If we found matching combinations, send the tracking event
          const totalValue = combinedObjectData.reduce((sum, item) => sum + item.price * item.quantity, 0);
          if (combinedObjectIDs.length) {
            insights('purchasedObjectIDsAfterSearch', {
              eventName: 'Conversion: Product Purchased after Search',
              index: AlgoliaIndice.PRODUCTS,
              objectIDs: combinedObjectIDs,
              queryID: addedToCart[0]?.queryID,
              // Use the first queryID as primary
              userToken: getGaUserId('_ga'),
              currency: getCurrency(),
              objectData: combinedObjectData,
              value: Math.round(totalValue * 100) / 100
            });
          }

          // Handle products that don't have algolia events or weren't added to cart (fallback)
          const unmatchedSkus = purchasedProducts.filter(item => !algoliaEventMap.has(item.sku)).map(item => item.sku);
          if (unmatchedSkus.length > 0) {
            insights('purchasedObjectIDs', {
              eventName: 'Conversion: Product Purchased',
              index: AlgoliaIndice.PRODUCTS,
              objectIDs: unmatchedSkus,
              userToken: getGaUserId('_ga'),
              currency: getCurrency()
            });
          }
        } else {
          // No algolia events - use original fallback logic
          const ids = purchasedProducts.map(({
            sku
          }) => sku);
          insights('purchasedObjectIDs', {
            eventName: 'Conversion: Product Purchased',
            index: AlgoliaIndice.PRODUCTS,
            objectIDs: ids,
            userToken: getGaUserId('_ga'),
            currency: getCurrency()
          });
        }
      } catch (e) {
        console.error('Error in algoliaCheckoutTrackClickEvent:', e);
      }
    }
  });
};
export const useAnalyticsTags = t0 => {
  const $ = _c(5);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const additionalTags = t1;
  const loginTag = isUserLoggedIn() ? "user_loggedin" : "user_loggedout";
  const deviceTag = useDeviceType().isMobile ? "web_mobile" : "web_desktop";
  let t2;
  if ($[2] !== additionalTags || $[3] !== deviceTag) {
    t2 = [deviceTag, loginTag, ...additionalTags];
    $[2] = additionalTags;
    $[3] = deviceTag;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};